// Course editor (/courses/new, /courses/:id): the course form (title,
// description, cover image in the courses Storage bucket, status) with the
// lesson tree below, like GuideEditorPage. The side panel previews the app's
// course card and shows the Firestore documents behind it.

import { useCallback, useEffect, useMemo, useRef, useState, type DragEvent } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { IconArrowLeft, IconTrash, IconUpload } from "@tabler/icons-react";
import { COURSE_STATUSES, emptyCourse, formatMinutes, plural, type Course, type CourseStatus, type Lesson } from "../types/course";
import { createCourse, deleteCourse, getCourseById, updateCourse, uploadImage } from "../services/courses/course.service";
import { getLessonsByCourseId } from "../services/courses/lesson.service";
import { errorText } from "../services/courses/auth";
import { useCoursesAuth } from "../hooks/useCoursesAuth";
import { StatusBadge } from "../components/StatusBadge";
import { CourseCover } from "../components/courses/CourseCover";
import { CoursesCallouts } from "../components/courses/CoursesCallouts";
import { LessonManager } from "../components/courses/LessonManager";
import { formatDateTime } from "../components/analytics/format";

type SideTab = "preview" | "document";

// The fields the form edits; everything else on the document is written by
// the services (totals, dates) or by other systems (tags, progress, ratings).
function formValues(c: Course) {
  return { title: c.title, description: c.description, imageUrl: c.imageUrl, status: c.status };
}

function capitalize(s: string) {
  return s[0].toUpperCase() + s.slice(1);
}

export function CourseEditorPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const auth = useCoursesAuth();
  const canEdit = !!auth.user;

  const [course, setCourse] = useState<Course>(emptyCourse());
  const [saved, setSaved] = useState<Course | null>(null);
  const [lessons, setLessons] = useState<Lesson[]>([]);
  const [loading, setLoading] = useState(!!id);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState<{ text: string; error?: boolean } | null>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [sideTab, setSideTab] = useState<SideTab>("preview");
  const fileInput = useRef<HTMLInputElement>(null);

  const load = useCallback(async (courseId: string) => {
    setLoadError(null);
    try {
      const [c, l] = await Promise.all([getCourseById(courseId), getLessonsByCourseId(courseId)]);
      if (!c) {
        setNotFound(true);
        return;
      }
      setCourse(c);
      setSaved(c);
      setLessons(l);
    } catch (e) {
      setLoadError(errorText(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    setStatus(null);
    setImageFile(null);
    setImagePreview(null);
    setNotFound(false);
    if (!id) {
      setCourse(emptyCourse());
      setSaved(null);
      setLessons([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    load(id);
  }, [id, load]);

  // Object URL for the picked file, released when it changes.
  useEffect(() => {
    if (!imageFile) return;
    const url = URL.createObjectURL(imageFile);
    setImagePreview(url);
    return () => URL.revokeObjectURL(url);
  }, [imageFile]);

  // Lesson and video writes update the totals on the course document too.
  const reloadLessons = useCallback(async () => {
    if (!id) return;
    try {
      const [c, l] = await Promise.all([getCourseById(id), getLessonsByCourseId(id)]);
      setLessons(l);
      if (c) {
        setSaved(c);
        // Keep unsaved form edits; refresh only what the services wrote.
        setCourse((cur) => ({ ...cur, totalLessons: c.totalLessons, totalDuration: c.totalDuration, updatedAt: c.updatedAt, tags: c.tags }));
      }
    } catch (e) {
      setStatus({ text: `Reload failed: ${errorText(e)}`, error: true });
    }
  }, [id]);

  const patch = (partial: Partial<Course>) => setCourse((c) => ({ ...c, ...partial }));

  const dirty = useMemo(() => {
    if (imageFile) return true;
    if (!saved) return !!(course.title || course.description || course.imageUrl);
    return JSON.stringify(formValues(course)) !== JSON.stringify(formValues(saved));
  }, [course, saved, imageFile]);

  function pickImage(file: File | undefined) {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setStatus({ text: "Please choose an image file.", error: true });
      return;
    }
    setImageFile(file);
  }

  function onDrop(e: DragEvent) {
    e.preventDefault();
    setDragOver(false);
    pickImage(e.dataTransfer.files?.[0]);
  }

  function removeImage() {
    setImageFile(null);
    setImagePreview(null);
    patch({ imageUrl: "" });
  }

  async function save() {
    const title = course.title.trim();
    const description = course.description.trim();
    if (!title) return setStatus({ text: "A title is required.", error: true });
    if (!description) return setStatus({ text: "A description is required.", error: true });

    setSaving(true);
    setStatus(null);
    try {
      let imageUrl = course.imageUrl;
      if (imageFile) {
        setStatus({ text: "Uploading image…" });
        imageUrl = await uploadImage(imageFile, "courses");
      }
      const data = { title, description, imageUrl: imageUrl || "", status: course.status };
      if (saved?.id) {
        // `tags` is written by the aura-course-rag Cloud Function; an update
        // must not overwrite it with the copy this page loaded earlier.
        await updateCourse(saved.id, data);
        setImageFile(null);
        setImagePreview(null);
        await load(saved.id);
        setStatus({ text: "Saved ✓" });
      } else {
        const newId = await createCourse({ ...data, tags: [] });
        navigate(`/courses/${newId}`, { replace: true });
      }
    } catch (e) {
      setStatus({ text: `Save failed: ${errorText(e)}`, error: true });
    } finally {
      setSaving(false);
    }
  }

  async function remove() {
    if (!saved?.id) return;
    const msg = `Delete “${saved.title || "Untitled course"}”? All ${plural(lessons.length, "lesson")} and their videos go with it. This cannot be undone.`;
    if (!confirm(msg)) return;
    try {
      await deleteCourse(saved.id);
      navigate("/courses");
    } catch (e) {
      setStatus({ text: `Delete failed: ${errorText(e)}`, error: true });
    }
  }

  if (loading) return <div className="state-box">Loading course…</div>;
  if (notFound || loadError) {
    return (
      <div className="list-page">
        <div className="state-box error">
          {notFound ? "Course not found." : loadError}
          <div style={{ marginTop: 16 }}>
            <button type="button" className="btn" onClick={() => navigate("/courses")}>
              <IconArrowLeft size={14} /> Courses
            </button>
          </div>
        </div>
      </div>
    );
  }

  const coverSource = imagePreview ? { imageUrl: imagePreview, title: course.title } : course;
  const totalDuration = course.totalDuration ?? 0;

  return (
    <div className="editor-page">
      <div className="editor-main">
        <button type="button" className="back-link co-back" onClick={() => navigate("/courses")}>
          <IconArrowLeft size={13} /> Courses
        </button>
        <div className="toolbar co-toolbar">
          <h1>{saved ? saved.title || <span className="muted">Untitled course</span> : "New course"}</h1>
          {saved && <StatusBadge status={saved.status} />}
          <div className="spacer" />
          <button type="button" className="btn btn-primary" onClick={save} disabled={!dirty || saving || !canEdit}>
            {saving ? "Saving…" : saved ? "Save" : "Create course"}
          </button>
          {saved && (
            <button type="button" className="co-btn-icon danger" onClick={remove} disabled={!canEdit || saving}
              title="Delete course" aria-label="Delete course">
              <IconTrash size={15} />
            </button>
          )}
        </div>
        <div className={`status-line co-status-line${status?.error ? " error" : ""}`}>
          {status ? (
            <span>{status.text}</span>
          ) : dirty ? (
            <span className="dirty">Unsaved changes</span>
          ) : saved ? (
            <>
              <span>Saved {formatDateTime(saved.updatedAt?.toISOString())}</span>
              <span>·</span>
              <span className="mono">courses/{saved.id}</span>
              {saved.createdBy && (
                <>
                  <span>·</span>
                  <span>created by {saved.createdBy}</span>
                </>
              )}
            </>
          ) : (
            <span>Fill in the course, then create it to add lessons.</span>
          )}
        </div>

        <CoursesCallouts auth={auth} />

        <fieldset disabled={!canEdit}>
          <legend>Course</legend>
          <label htmlFor="co-title" style={{ marginTop: 0 }}>Title</label>
          <input id="co-title" type="text" value={course.title} placeholder="e.g. Learning to meditate"
            onChange={(e) => patch({ title: e.target.value })} />
          <label htmlFor="co-description">Description</label>
          <textarea id="co-description" rows={3} value={course.description}
            placeholder="One or two sentences the app shows under the title."
            onChange={(e) => patch({ description: e.target.value })} />
          <label style={{ marginTop: 12 }}>Image</label>
          <div className="co-row-img">
            <div>
              <div
                className={`co-dropzone${dragOver ? " drag-over" : ""}`}
                title="Click or drop an image"
                role="button"
                tabIndex={0}
                aria-disabled={!canEdit}
                onClick={() => canEdit && fileInput.current?.click()}
                onKeyDown={(e) => { if (e.key === "Enter" && canEdit) fileInput.current?.click(); }}
                onDragOver={(e) => { e.preventDefault(); if (canEdit) setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={(e) => (canEdit ? onDrop(e) : e.preventDefault())}
              >
                {coverSource.imageUrl ? (
                  <CourseCover course={coverSource} />
                ) : (
                  <div className="empty">
                    <span>
                      <IconUpload size={18} />
                      <br />
                      Drop a 16:9 image here
                      <br />
                      or click to choose
                    </span>
                  </div>
                )}
              </div>
              <input ref={fileInput} type="file" accept="image/*" hidden aria-label="Course image"
                onChange={(e) => { pickImage(e.target.files?.[0]); e.target.value = ""; }} />
              <div className="co-img-actions">
                <button type="button" className="btn btn-sm" onClick={() => fileInput.current?.click()}>
                  <IconUpload size={13} /> {coverSource.imageUrl ? "Replace" : "Upload image"}
                </button>
                {coverSource.imageUrl && (
                  <button type="button" className="btn btn-sm btn-ghost" onClick={removeImage}>
                    Remove
                  </button>
                )}
              </div>
              <div className="co-img-meta">
                {imageFile
                  ? `${imageFile.name} · uploads to the aura-courses bucket on Save`
                  : course.imageUrl
                    ? course.imageUrl
                    : "No image. The app shows a plain card. Recommended 16:9."}
              </div>
            </div>
            <div>
              <label style={{ marginTop: 0 }}>Tags</label>
              <div className="co-tag-field">
                {(course.tags ?? []).length ? course.tags!.map((t) => <span key={t} className="chip">{t}</span>) : "None yet."}
              </div>
              <div className="co-hint">
                Written by the <span className="mono">aura-course-rag</span> Cloud Function from the title and
                description after each save. Not edited here.
              </div>
              <label htmlFor="co-status" style={{ marginTop: 12 }}>Status</label>
              <select id="co-status" value={course.status} onChange={(e) => patch({ status: e.target.value as CourseStatus })}>
                {COURSE_STATUSES.map((s) => (
                  <option key={s} value={s}>{capitalize(s)}</option>
                ))}
              </select>
              <div className="co-hint">Only published courses appear in the app. Any admin can set any status.</div>
            </div>
          </div>
        </fieldset>

        <fieldset>
          <legend>
            Lessons <span className="count">{lessons.length}</span>
            {saved && <span>· {formatMinutes(totalDuration)}</span>}
          </legend>
          {saved?.id ? (
            <LessonManager courseId={saved.id} lessons={lessons} onChanged={reloadLessons} disabled={!canEdit} />
          ) : (
            <div className="callout">Create the course first, then add lessons and videos here.</div>
          )}
        </fieldset>
      </div>

      <aside className="editor-side">
        <div className="tabs">
          <button type="button" className={sideTab === "preview" ? "active" : ""} onClick={() => setSideTab("preview")}>
            App preview
          </button>
          <button type="button" className={sideTab === "document" ? "active" : ""} onClick={() => setSideTab("document")}>
            Firestore document
          </button>
        </div>
        <div className="side-body co-side-body">
          {sideTab === "preview" ? (
            <>
              <div className="co-phone">
                <CourseCover course={coverSource} />
                <div className="co-phone-body">
                  <h3>{course.title || "Untitled course"}</h3>
                  <div className="meta">
                    <span>{plural(lessons.length, "lesson")}</span>
                    <span>·</span>
                    <span>{formatMinutes(totalDuration)}</span>
                  </div>
                  {(course.tags ?? []).length > 0 && (
                    <div className="co-card-tags">
                      {course.tags!.map((t) => <span key={t} className="chip">{t}</span>)}
                    </div>
                  )}
                  <p>{course.description || "No description."}</p>
                  <div>
                    {lessons.length === 0 ? (
                      <div className="co-phone-lesson muted">No lessons yet</div>
                    ) : (
                      lessons.map((l, i) => (
                        <div key={l.id} className="co-phone-lesson">
                          <span className="n">{i + 1}</span>
                          <div style={{ minWidth: 0, flex: 1 }}>
                            <div>{l.title}</div>
                            <div className="s">
                              {formatMinutes(l.totalDuration)}
                              {l.knowledgeCheck ? " · knowledge check" : ""}
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                  <div className="co-phone-cta">{course.status === "published" ? "Start course" : "Not in the app yet"}</div>
                </div>
              </div>
              <div className="co-api-note">
                The app reads <span className="k">courses</span> where status == "published", ordered by createdAt, plus
                the lessons and videos subcollections.
                {course.status !== "published" ? ` This course is ${course.status}, so it is hidden.` : ""}
              </div>
            </>
          ) : (
            <>
              <div className="co-doc-path">courses/{saved?.id ?? "(new)"}</div>
              <pre className="co-doc-view">
                {JSON.stringify(
                  {
                    title: course.title,
                    description: course.description,
                    imageUrl: course.imageUrl,
                    status: course.status,
                    tags: course.tags ?? [],
                    totalLessons: course.totalLessons ?? 0,
                    totalDuration: totalDuration,
                    createdAt: saved?.createdAt?.toISOString() ?? null,
                    updatedAt: saved?.updatedAt?.toISOString() ?? null,
                    createdBy: saved?.createdBy ?? null,
                  },
                  null,
                  2,
                )}
              </pre>
              {lessons.map((l) => (
                <div key={l.id}>
                  <div className="co-doc-path">└ lessons/{l.id}</div>
                  <pre className="co-doc-view">
                    {JSON.stringify(
                      {
                        title: l.title,
                        description: l.description ?? "",
                        order: l.order,
                        totalDuration: l.totalDuration ?? 0,
                        ...(l.knowledgeCheck ? { knowledgeCheck: l.knowledgeCheck } : {}),
                      },
                      null,
                      2,
                    )}
                  </pre>
                </div>
              ))}
              <div className="co-api-note">
                Videos live under each lesson (lessons/{"{id}"}/videos). totalLessons and totalDuration are recomputed
                client-side after every lesson or video change, as the old admin did. Durations are minutes.
              </div>
            </>
          )}
        </div>
      </aside>
    </div>
  );
}
